import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Database } from 'lucide-react';
import { needsEmailVerification, useAuth } from '../context/AuthContext';
import { useCredits } from '../hooks/useCredits';
import { isAdminEmail } from '../lib/userLimits';
import { useIsInsideAppLayout } from './AppLayoutContext';

const getAccountInitial = (user) => {
  const name = user?.displayName?.trim();
  const email = user?.email?.trim();
  return (name?.[0] || email?.[0] || 'U').toUpperCase();
};

function Navbar({ onMobileMenuOpenChange } = {}) {
  const isInsideAppLayout = useIsInsideAppLayout();
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const headerRef = useRef(null);
  const accountWrapRef = useRef(null);
  const currentPath = location.pathname;
  const isLoginPage = currentPath === '/login';
  const isRegisterPage = currentPath === '/register';
  const isAuthSheetPage =
    isLoginPage || isRegisterPage;
  const isVerifiedUser = isAuthenticated && !needsEmailVerification(user);
  const credits = useCredits();
  const adminUser = isAdminEmail(user?.email);
  const brandTarget = '/';
  const menuButtonLabel = isMobileMenuOpen ? 'Close menu' : 'Open menu';

  const scrollHomeHashIfAlreadyOnHash = (hash, elementId) => {
    if (location.pathname === '/' && location.hash === hash) {
      document.getElementById(elementId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsAccountMenuOpen(false);
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    onMobileMenuOpenChange?.(isMobileMenuOpen);
  }, [isMobileMenuOpen, onMobileMenuOpenChange]);

  useEffect(() => {
    if (!isAccountMenuOpen) return undefined;

    const handleOutsideClick = (e) => {
      if (accountWrapRef.current && !accountWrapRef.current.contains(e.target)) {
        setIsAccountMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [isAccountMenuOpen]);

  useEffect(() => {
    if (!isMobileMenuOpen) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (headerRef.current && !headerRef.current.contains(event.target)) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [isMobileMenuOpen]);

  if (isInsideAppLayout) {
    return null;
  }

  return (
    <header className="site-header" ref={headerRef}>
      <div className="container nav-wrap">
        <div className="nav-wrap-start">
          <Link to={brandTarget} className="brand">
            <img
              src="/images/navbar-logo.png?v=20260615-1"
              alt="MyRoomsAI"
              className="brand-logo"
              loading="lazy"
              decoding="async"
              width={600}
              height={91}
            />
          </Link>
          <button
            type="button"
            className={`mobile-menu-toggle${isMobileMenuOpen ? ' is-open' : ''}`}
            aria-label={menuButtonLabel}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-main-menu"
            onClick={() => setIsMobileMenuOpen((prev) => !prev)}
          >
            <span />
            <span />
            <span />
          </button>
        </div>
        <nav className="nav-links" aria-label="Main navigation">
          {!isAuthenticated ? (
            <>
              <Link to="/" className="nav-link-btn">
                Home
              </Link>
              <Link
                to="/#pricing"
                className="nav-link-btn"
                onClick={() => scrollHomeHashIfAlreadyOnHash('#pricing', 'pricing')}
              >
                Pricing
              </Link>
              <Link to="/ai-tools/ai-room-decoration" className="nav-link-btn">
                AI Tools
              </Link>
              <Link to="/features" className="nav-link-btn">
                Features
              </Link>
            </>
          ) : null}
          {isVerifiedUser ? (
            <>
              {!isAuthSheetPage ? (
                <Link to="/projects">Projects</Link>
              ) : null}
              {!isAuthSheetPage ? (
                <Link to="/ai-tools/ai-room-decoration">
                  AI Tools
                </Link>
              ) : null}
              {!isAuthSheetPage ? (
                <Link to="/credits">Credits</Link>
              ) : null}
              {!isAuthSheetPage ? (
                <Link to="/" className="nav-link-home">
                  Home
                </Link>
              ) : null}
            </>
          ) : null}
        </nav>
        <div className={`nav-wrap-end${!isAuthenticated ? ' nav-wrap-end--guest' : ''}`}>
          {isVerifiedUser ? (
            <div className="nav-account-area">
              {!isAuthSheetPage ? (
                <Link
                  to="/credits"
                  className="nav-credits-pill"
                  aria-label={
                    credits !== null
                      ? `Credits, ${credits} available. Go to credits page.`
                      : 'Credits. Go to credits page.'
                  }
                >
                  <span className="nav-credits-pill-value">
                    {credits !== null ? credits : '…'}
                  </span>
                  <Database size={16} strokeWidth={2} aria-hidden />
                </Link>
              ) : null}
              <div className="nav-avatar-wrap" ref={accountWrapRef}>
                <button
                  type="button"
                  className="nav-avatar"
                  aria-label="Account menu"
                  aria-expanded={isAccountMenuOpen}
                  onClick={() => setIsAccountMenuOpen((prev) => !prev)}
                >
                  {getAccountInitial(user)}
                </button>
                {isAccountMenuOpen && (
                  <div className="nav-account-dropdown">
                    <div className="nav-account-dropdown-header">
                      <span className="nav-account-dropdown-label">Signed in as</span>
                      <span className="nav-account-dropdown-email">{user?.email}</span>
                    </div>
                    <div className="nav-account-dropdown-divider" />
                    {adminUser ? (
                      <Link
                        to="/admin"
                        className="nav-account-dropdown-item"
                        onClick={() => setIsAccountMenuOpen(false)}
                      >
                        Admin
                      </Link>
                    ) : null}
                    <button
                      type="button"
                      className="nav-account-dropdown-item nav-account-dropdown-logout"
                      onClick={() => { setIsAccountMenuOpen(false); logout(); }}
                    >
                      Log out
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : isAuthenticated ? (
            <div className="nav-account-area">
              <div className="nav-avatar-wrap" ref={accountWrapRef}>
                <button
                  type="button"
                  className="nav-avatar"
                  aria-label="Account menu"
                  aria-expanded={isAccountMenuOpen}
                  onClick={() => setIsAccountMenuOpen((prev) => !prev)}
                >
                  {getAccountInitial(user)}
                </button>
                {isAccountMenuOpen && (
                  <div className="nav-account-dropdown">
                    <div className="nav-account-dropdown-header">
                      <span className="nav-account-dropdown-label">Signed in as</span>
                      <span className="nav-account-dropdown-email">{user?.email}</span>
                    </div>
                    <div className="nav-account-dropdown-divider" />
                    {adminUser ? (
                      <Link
                        to="/admin"
                        className="nav-account-dropdown-item"
                        onClick={() => setIsAccountMenuOpen(false)}
                      >
                        Admin
                      </Link>
                    ) : null}
                    <button
                      type="button"
                      className="nav-account-dropdown-item nav-account-dropdown-logout"
                      onClick={() => { setIsAccountMenuOpen(false); logout(); }}
                    >
                      Log out
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <>
              {!isLoginPage ? (
                <Link to="/login" className="btn btn-ghost">
                  Login
                </Link>
              ) : null}
              {!isRegisterPage ? (
                <Link to="/register" className="btn btn-primary">
                  Get Started
                </Link>
              ) : null}
            </>
          )}
        </div>
      </div>
      <nav
        id="mobile-main-menu"
        className={`mobile-menu${isMobileMenuOpen ? ' open' : ''}`}
        aria-label="Mobile navigation"
      >
        <div className="container mobile-menu-inner">
          {!isAuthenticated ? (
            <>
              <Link
                to="/"
                className="mobile-menu-link"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Home
              </Link>
              <Link
                to="/#pricing"
                className="mobile-menu-link"
                onClick={() => {
                  scrollHomeHashIfAlreadyOnHash('#pricing', 'pricing');
                  setIsMobileMenuOpen(false);
                }}
              >
                Pricing
              </Link>
              <Link
                to="/ai-tools/ai-room-decoration"
                className="mobile-menu-link"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                AI Tools
              </Link>
              <Link
                to="/features"
                className="mobile-menu-link"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Features
              </Link>
              {!isLoginPage ? (
                <div className="mobile-menu-auth-actions">
                  <Link
                    to="/login"
                    className="btn btn-ghost mobile-menu-auth-btn"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Login
                  </Link>
                  {!isRegisterPage ? (
                    <Link
                      to="/register"
                      className="btn btn-primary mobile-menu-auth-btn"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Get Started
                    </Link>
                  ) : null}
                </div>
              ) : !isRegisterPage ? (
                <div className="mobile-menu-auth-actions">
                  <Link
                    to="/register"
                    className="btn btn-primary mobile-menu-auth-btn"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Get Started
                  </Link>
                </div>
              ) : null}
            </>
          ) : isVerifiedUser ? (
            <>
              <Link
                to="/"
                className="mobile-menu-link"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Home
              </Link>
              <Link
                to="/projects"
                className="mobile-menu-link"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Projects
              </Link>
              <Link
                to="/ai-tools/ai-room-decoration"
                className="mobile-menu-link"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                AI Tools
              </Link>
              <Link
                to="/credits"
                className="mobile-menu-link"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Credits
              </Link>
              {adminUser ? (
                <Link
                  to="/admin"
                  className="mobile-menu-link"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Admin
                </Link>
              ) : null}
              <div className="mobile-account-row">
                <button
                  type="button"
                  className="btn btn-primary mobile-menu-cta"
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    logout();
                  }}
                >
                  Logout
                </button>
                <span className="nav-avatar" aria-label="Account">
                  {getAccountInitial(user)}
                </span>
              </div>
            </>
          ) : (
            <>
              <Link to="/verify-email" className="mobile-menu-link" onClick={() => setIsMobileMenuOpen(false)}>
                Verify Email
              </Link>
              <div className="mobile-account-row">
                <button
                  type="button"
                  className="btn btn-primary mobile-menu-cta"
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    logout();
                  }}
                >
                  Logout
                </button>
                <span className="nav-avatar" aria-label="Account">
                  {getAccountInitial(user)}
                </span>
              </div>
            </>
          )}
        </div>
      </nav>
    </header>
  );
}

export default Navbar;
